import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Siren, ChevronLeft, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

export function EvacuationHistory() {
  // Phase B2 — locale-aware evacuation timestamps.
  const { formatDateTime } = useLocale();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/evacuation/events", { params: { limit: 100 } })
      .then((r) => setEvents(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  return (
    <AppShell>
      <Link to="/evacuation" className="btn-ghost text-xs mb-3" data-testid="hist-back">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </Link>
      <h1 className="text-3xl font-semibold flex items-center gap-3" data-testid="hist-heading">
        <Siren className="w-7 h-7 text-red-600" /> Evacuation history
      </h1>
      <p className="text-sm text-navy-500 mt-1 mb-5">All past evacuation events with their post-incident reports.</p>

      <div className="eb-card p-0 overflow-hidden">
        {loading ? (
          <p className="py-10 text-center text-navy-500">Loading…</p>
        ) : events.length === 0 ? (
          <p className="py-16 text-center text-sm text-navy-400">No events yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-navy-50/60 text-navy-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Started</th>
                <th className="text-left px-4 py-3">Ended</th>
                <th className="text-left px-4 py-3">Sites</th>
                <th className="text-left px-4 py-3">Missing</th>
                <th className="text-left px-4 py-3">Duration</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-navy-100" data-testid={`hist-row-${e.id}`}>
                  <td className="px-4 py-3">
                    {e.status === "active" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-red-100 text-red-700">
                        <AlertTriangle className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1 rounded-full bg-navy-100 text-navy-600">
                        Ended
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{e.started_at ? formatDateTime(e.started_at) : "—"}</td>
                  <td className="px-4 py-3">{e.ended_at ? formatDateTime(e.ended_at) : "—"}</td>
                  <td className="px-4 py-3">{e.site_ids?.length || 0}</td>
                  <td className="px-4 py-3">
                    {e.missing_count != null ? (
                      <span className={e.missing_count > 0 ? "text-danger font-bold" : "text-success"}>
                        {e.missing_count}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-navy-500">
                    {e.duration_seconds ? `${Math.round(e.duration_seconds / 60)}m` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/evacuation/history/${e.id}`} className="text-orange-600 hover:underline text-xs"
                      data-testid={`hist-view-${e.id}`}>View →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}

export function EvacuationHistoryDetail() {
  // Phase B2 — locale-aware tick-log timestamps.
  const { formatDateTime } = useLocale();
  const { eventId } = useParams();
  const [evt, setEvt] = useState(null);
  useEffect(() => {
    api.get(`/evacuation/events/${eventId}`)
      .then((r) => setEvt(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [eventId]);

  if (!evt) {
    return <AppShell><p className="py-10 text-center text-navy-500">Loading…</p></AppShell>;
  }
  const snap = evt.end_snapshot || {};
  return (
    <AppShell>
      <Link to="/evacuation/history" className="btn-ghost text-xs mb-3"><ChevronLeft className="w-3.5 h-3.5" /> Back</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="eb-overline mb-2">Module 10.7 · Post-incident report</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" data-testid="evt-detail-heading">
            <Siren className="w-6 h-6 text-red-600" />
            Evacuation event <code className="text-sm">{evt.id?.slice(0, 8)}…</code>
          </h1>
          <p className="text-sm text-navy-500 mt-1">
            Status: <strong>{evt.status}</strong> · started by {evt.started_by_label}
          </p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/evacuation/${evt.id}/printable.pdf`}
          target="_blank" rel="noreferrer"
          className="btn-outline text-xs" data-testid="evt-detail-pdf">PDF</a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi icon={Clock} label="Started" value={evt.started_at?.slice(0, 16)?.replace("T", " ")} />
        <Kpi icon={Clock} label="Ended" value={evt.ended_at?.slice(0, 16)?.replace("T", " ") || "—"} />
        <Kpi icon={CheckCircle2} label="Accounted for" value={`${snap.accounted_for ?? 0}/${snap.total_signed_in ?? 0}`} accent="text-success" />
        <Kpi icon={AlertTriangle} label="Missing" value={snap.missing_count ?? "—"} accent={snap.missing_count > 0 ? "text-danger" : "text-success"} />
      </div>

      <div className="eb-card p-5 mb-5">
        <h3 className="font-semibold mb-2">Reason & summary</h3>
        <p className="text-sm text-navy-700 mb-2"><strong>Reason:</strong> {evt.ended_reason || evt.reason || "—"}</p>
        <p className="text-sm text-navy-700"><strong>Summary:</strong> {evt.ended_summary || "—"}</p>
      </div>

      {snap.missing && snap.missing.length > 0 && (
        <div className="eb-card p-5 mb-5 border-l-4 border-danger" data-testid="evt-missing-list">
          <h3 className="font-semibold mb-3 text-danger inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Missing ({snap.missing.length})
          </h3>
          <ul className="text-sm divide-y divide-navy-100">
            {snap.missing.map((m) => (
              <li key={m.id} className="py-2">
                <strong>{m.visitor_name}</strong>
                {m.company ? <span className="text-navy-500"> · {m.company}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {snap.tick_log && snap.tick_log.length > 0 && (
        <div className="eb-card p-5">
          <h3 className="font-semibold mb-3">Tick log ({snap.tick_log.length})</h3>
          <ul className="text-xs divide-y divide-navy-100 max-h-[400px] overflow-y-auto">
            {snap.tick_log.map((t) => (
              <li key={t.attendance_id} className="py-2 flex items-center justify-between">
                <div>
                  <div>{t.visitor_name} <span className="text-navy-400">→ {t.status}</span></div>
                  <div className="text-navy-400">{t.accounted_for_at ? `${formatDateTime(t.accounted_for_at)} · by ${t.accounted_for_by}` : "not accounted for"}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AppShell>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="eb-card p-3 flex items-start gap-2">
      <Icon className="w-4 h-4 text-navy-400 mt-0.5" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-navy-400 font-bold">{label}</div>
        <div className={`text-base font-semibold ${accent || "text-navy-900"}`}>{value}</div>
      </div>
    </div>
  );
}
